import React, { useState, useEffect, useRef } from 'react';
import { Grid, Card, Box, CardContent, Typography, MenuItem, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Ministry from '../ministry';
import LGD from '../lgd';
import Event from '../event';
import axios from 'axios';
import { useSelector } from 'react-redux';
import TableComponent from '../../components/table';
import { getAllEventsByQuery, getAllEventTheme } from '../../firebase/functions';
import Spinner from '../../views/spinner/Spinner';
import CustomFormLabel from '../forms/custom-elements/CustomFormLabel';
import CustomSelect from '../forms/custom-elements/CustomSelect';
import { isNationalScopeRole } from '../../constants/roles';
import { getCtuReport2025, getEventTypeReport } from '../../api';
import {
  isApiReportYear,
  getThemeOptions,
  getEventTypeReportColumns,
} from '../../constants/reportYears';

const MinistryWiseReport = ({ setIsLoading, year }) => {
  // The API years default to 'all' (CTU & Clean Public Spaces); 2024 leaves the
  // theme unset and takes it from the Event dropdown instead.
  const [selectedEventThemeCode, setSelectedEventThemeCode] = useState(
    isApiReportYear(year) ? 'all' : undefined
  );
  const [selectedEventNameCode, setSelectedEventNameCode] = useState(undefined);
  const [selectedEventHeadingCode, setSelectedEventHeadingCode] = useState(undefined);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedStateCode, setSelectedStateCode] = useState(undefined);
  const [selectedDistrictCode, setSelectedDistrictCode] = useState(undefined);
  const [selectedBlockCode, setSelectedBlockCode] = useState(undefined);
  const [selectedGPCode, setSelectedGPCode] = useState(undefined);
  const [selectedVillageCode, setSelectedVillageCode] = useState(undefined);
  const [selectedULBCode, setSelectedULBCode] = useState(undefined);
  const [ministryCode, setMinistryCode] = useState(undefined);
  const [ministryDepartmentCode, setMinistryDepartmentCode] = useState(undefined);
  const [ministrySubUnitCode, setMinistrySubUnitCode] = useState(undefined);
  const [selectedCtuStatus, setSelectedCtuStatus] = useState(undefined);
  const [selectedAreaType, setSelectedAreaType] = useState(undefined);
  const [tableData, setTableData] = useState([]);
  const [headers, setHeaders] = useState({});

  // Only the newest report fetch may write to the table.
  //
  // getReports / getReports2025 / getEventTypeReports2025 each clear the table,
  // await, then write. With no cancellation, two overlapping runs -- a filter
  // changed mid-request, View Report pressed twice, a tab switched -- resolve in
  // whatever order the network returns them, and the LAST to land wins. That is
  // how a request that genuinely returned rows ends up showing "No data yet":
  // an older request finished afterwards and took the empty branch. The same
  // race silently shows a previous filter's numbers when the older response
  // happens to be non-empty, which is harder to notice.
  const latestReportFetchRef = useRef(0);
  const beginReportFetch = () => {
    latestReportFetchRef.current += 1;
    return latestReportFetchRef.current;
  };
  const isStaleReport = (ticket) => latestReportFetchRef.current !== ticket;
  const role = useSelector((state) => state.RoleReducer);
  const [filters, setFilters] = useState([]);

  // Area Types from add-event form
  const areaTypes = [
    'Abandon or open land with dumping',
    'Area near Transfer Station',
    'Back lanes',
    'Cleaning of Water Tank',
    'Construction Debris Areas',
    'Drainage, Nallahs',
    'Garbage Dump',
    'Market spaces, Mandis',
    'Railway tracks',
    'Roadsides (NH, SH, Local)',
    'Slums',
    'Waterbodies',
  ];
  const [events, setEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const pageSize = 500;
  const [lastDoc, setLastDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const FILTER_EMPTY_MINISTRY_NAME = true; // Set to false to disable filtering

  useEffect(() => {
    getFilteredData(true);
  }, [filters]);

  const getFilteredData = async (resetData = false) => {
    if (Object.keys(filters).length > 0) {
      setLoading(true);
      const filteredEvents = await getAllEventsByQuery(
        resetData ? null : lastDoc,
        pageSize,
        filters
      );
      if (filteredEvents) {
        if (resetData) {
          setEvents(filteredEvents.data);
        } else {
          const uniqueEvents = [
            ...new Map(
              [...events, ...filteredEvents.data].map((event) => [event.doc_id, event])
            ).values(),
          ];
          setEvents(uniqueEvents);
        }
        setLastDoc(filteredEvents.lastDoc);
      }
    }
    setLoading(false);
  };

  // Handler for Event Theme Name change - clears other filters
  const handleEventThemeChange = (e) => {
    const newEventThemeCode = e.target.value;
    setSelectedEventThemeCode(newEventThemeCode);

    // Clear Area Type when switching away from CTU & CPS
    if (newEventThemeCode !== 'all') {
      setSelectedAreaType(undefined);
    }

    // Clear dependent filters when theme changes
    setSelectedEventHeadingCode(undefined);
    setSelectedEventNameCode(undefined);
  };

  // The theme dropdown only exists on the API years; 2024 filters by the Event
  // theme/category/type dropdowns instead. Both years' option lists are built
  // by getThemeOptions -- see the note there.
  const getEventTheme = async () => {
    if (!isApiReportYear(year)) return;

    try {
      const types = await getAllEventTheme();
      setEventTypes(getThemeOptions(year, types));
    } catch (error) {
      console.error('Error fetching event themes:', error);
    }
  };

  const filterEvents = async () => {
    const newFilters = {};
    if (selectedEventThemeCode) newFilters.event_theme_id = selectedEventThemeCode;
    if (selectedEventNameCode) newFilters.event_name_id = selectedEventNameCode;
    if (selectedEventHeadingCode) newFilters.event_heading_id = selectedEventHeadingCode;
    if (selectedLocation) newFilters.location_type = selectedLocation;
    if (selectedStateCode) newFilters.state_code = selectedStateCode;
    if (selectedDistrictCode) newFilters.district_code = selectedDistrictCode;
    if (selectedBlockCode) newFilters.block_code = selectedBlockCode;
    if (selectedGPCode) newFilters.gp_code = selectedGPCode;
    if (selectedVillageCode) newFilters.village_code = selectedVillageCode;
    if (selectedULBCode) newFilters.ulb_code = selectedULBCode;
    if (ministryCode) newFilters.ministry_code = ministryCode;
    if (ministryDepartmentCode) newFilters.ministry_department_code = ministryDepartmentCode;
    if (ministrySubUnitCode) newFilters.ministry_unit_code = ministrySubUnitCode;
    if (selectedCtuStatus !== undefined) newFilters.event_completed = selectedCtuStatus;
    setLastDoc(null);
    setFilters(newFilters);
  };

  useEffect(() => {
    if (selectedEventNameCode === 34 && ministrySubUnitCode) {
      filterEvents();
    }
  }, [
    ministrySubUnitCode,
    selectedEventNameCode,
    selectedLocation,
    selectedStateCode,
    selectedDistrictCode,
    selectedBlockCode,
    selectedULBCode,
    selectedGPCode,
    selectedVillageCode,
    selectedCtuStatus,
  ]);

  const allRow = {
    view_event: 'View Event',
    event_user_name: 'CTU Name',
    location_type: 'Location Type',
    state_name: 'State Name',
    district_name: 'District Name',
    ulb_name: 'ULB Name',
    block_name: 'Block Name',
    gp_name: 'GP Name',
    ministry_name: 'Ministry Name',
    ministry_department_name: 'Ministry Department Name',
    ministry_unit_name: 'Ministry Unit Department Name',
    village_name: 'Village Name',
    event_start_date: 'Event Date (YYYY-MM-DD)',
    event_coordinator_name: 'Event Coordinator',
    event_completed: 'Completed',
  };

  const downloadRow = {
    event_theme_name: 'Event Theme Name',
    event_heading_name: 'Event Category',
    event_name: 'Event Type',
    event_user_name: 'CTU Name',
    location_type: 'Location Type',
    state_name: 'State Name',
    district_name: 'District Name',
    ulb_name: 'ULB Name',
    block_name: 'Block Name',
    gp_name: 'GP Name',
    village_name: 'Village Name',
    ministry_name: 'Ministry Name',
    ministry_department_name: 'Ministry Department Name',
    ministry_unit_name: 'Ministry Unit Department Name',
    event_start_date: 'Event Date (YYYY-MM-DD)',
    event_coordinator_name: 'Event Coordinator',
    event_completed: 'Completed',
  };

  // Function to merge duplicate ministry codes by adding numeric values
  const mergeDuplicateMinistries = (data) => {
    const ministryMap = new Map();

    data.forEach((item) => {
      const ministryKey = item.ministry_code;

      if (ministryMap.has(ministryKey)) {
        // Merge with existing entry
        const existingItem = ministryMap.get(ministryKey);
        const mergedItem = { ...existingItem };

        // Add numeric values for duplicate ministry codes
        Object.keys(item).forEach((key) => {
          if (
            key !== 'ministry_code' &&
            key !== 'ministry_name' &&
            key !== 'ministry_department_name' &&
            key !== 'ministry_unit_name'
          ) {
            const existingValue = parseFloat(existingItem[key]) || 0;
            const newValue = parseFloat(item[key]) || 0;
            mergedItem[key] = existingValue + newValue;
          }
        });

        ministryMap.set(ministryKey, mergedItem);
      } else {
        // First occurrence of this ministry code
        ministryMap.set(ministryKey, { ...item });
      }
    });

    return Array.from(ministryMap.values());
  };

  const getReports = async () => {
    const reportTicket = beginReportFetch();
    setIsLoading(true);
    setTableData([]);
    const baseUrl = `https://asia-south1-dashakswachhataka.cloudfunctions.net/getReportsLatest?resource=events&labels=["total_count"]&modifiers=["count"]&rank_by=ministry_name`;

    let filters = [];
    let categories = [];
    let allHeaders = {};
    let fields = [
      '*',
      `SUM(CASE WHEN event_theme_id = '1' THEN 1 ELSE 0 END) AS Swachhata_Mein_Jan_Bhaagidari`,
      `SUM(CASE WHEN event_theme_id = '3' THEN 1 ELSE 0 END) AS SafaiMitra_Suraksha_Shivirs`,
      `SUM(CASE WHEN event_name_id = '34' THEN 1 ELSE 0 END) AS CTU_Identified`,
      `SUM(CASE WHEN event_name_id = '34' AND event_completed = TRUE THEN 1 ELSE 0 END) AS CTU_Transformed`,
      `SUM(CASE WHEN event_name_id = '34' AND event_completed = FALSE THEN 1 ELSE 0 END) AS CTU_Pending`,
      `SUM(CASE WHEN event_type = 'PARTNER_ADOPT_CTU' THEN 1 ELSE 0 END) AS CTU_Adopted`,
      `SUM(CASE WHEN event_type = 'PARTNER_JOIN_CTU' THEN 1 ELSE 0 END) AS CTU_Joined`,
    ];
    let clickhouse_fields = [
      '*',
      `SUM(if(event_theme_id = '1', 1, 0)) AS Swachhata_Mein_Jan_Bhaagidari`,
      `SUM(if(event_theme_id = '3', 1, 0)) AS SafaiMitra_Suraksha_Shivirs`,
      `SUM(if(event_name_id = '34', 1, 0)) AS CTU_Identified`,
      ` SUM(
    IF(
      (event_name_id = '34') AND (event_completed = 'true'),
      1,
      0
    )
  ) AS CTU_Transformed`,
      ` SUM(
    IF(
      (event_name_id = '34') AND (event_completed = 'false'),
      1,
      0
    )
  ) AS CTU_Pending`,
      `SUM(if(event_type = 'PARTNER_ADOPT_CTU', 1, 0)) AS CTU_Adopted`,
      `SUM(if(event_type = 'PARTNER_JOIN_CTU', 1, 0)) AS CTU_Joined`,
    ];

    if (selectedEventThemeCode) {
      filters.push(`event_theme_id='${selectedEventThemeCode}'`);
    }

    if (selectedEventHeadingCode) {
      filters.push(`event_heading_id='${selectedEventHeadingCode}'`);
    }

    if (selectedEventNameCode) {
      filters.push(`event_name_id='${selectedEventNameCode}'`);
    }

    if (selectedLocation) {
      filters.push(`location_type='${selectedLocation}'`);
    }

    if (selectedStateCode) {
      filters.push(`state_code='${selectedStateCode}'`);
    }

    if (selectedDistrictCode) {
      filters.push(`district_code='${selectedDistrictCode}'`);
    }

    if (selectedBlockCode) {
      filters.push(`block_code='${selectedBlockCode}'`);
    }

    if (selectedULBCode) {
      filters.push(`ulb_code='${selectedULBCode}'`);
    }

    if (selectedGPCode) {
      filters.push(`gp_code='${selectedGPCode}'`);
    }

    if (selectedVillageCode) {
      filters.push(`village_code='${selectedVillageCode}'`);
    }

    if (ministryCode) {
      filters.push(`ministry_code='${ministryCode}'`);
      categories.push('ministry_code');
      categories.push('ministry_name');
      categories.push('ministry_department_code');
      categories.push('ministry_department_name');
      fields.push('ministry_code');
      fields.push('ministry_name');
      fields.push('ministry_department_code');
      fields.push('ministry_department_name');
      clickhouse_fields.push('ministry_code');
      clickhouse_fields.push('ministry_name');
      clickhouse_fields.push('ministry_department_code');
      clickhouse_fields.push('ministry_department_name');
      allHeaders = {
        ...allHeaders,
        ministry_name: 'Ministry Name',
        ministry_department_name: 'Ministry Department Name',
      };
    } else {
      filters.push("ministry_code!=''");
      categories.push('ministry_code');
      categories.push('ministry_name');
      fields.push('ministry_code');
      fields.push('ministry_name');
      clickhouse_fields.push('ministry_code');
      clickhouse_fields.push('ministry_name');
      allHeaders = {
        ...allHeaders,
        ministry_name: 'Ministry Name',
      };
    }

    if (ministryDepartmentCode) {
      filters.push(`ministry_department_code='${ministryDepartmentCode}'`);
      categories.push('ministry_unit_code');
      categories.push('ministry_unit_name');
      fields.push('ministry_unit_code');
      fields.push('ministry_unit_name');
      clickhouse_fields.push('ministry_unit_code');
      clickhouse_fields.push('ministry_unit_name');
      allHeaders = {
        ...allHeaders,
        ministry_name: 'Ministry Name',
        ministry_department_name: 'Ministry Department Name',
        ministry_unit_name: 'Ministry Unit Name',
      };
    }

    if (ministrySubUnitCode) {
      filters.push(`ministry_unit_code='${ministrySubUnitCode}'`);
    }

    if (selectedCtuStatus !== undefined) {
      filters.push(`event_completed='${selectedCtuStatus}'`);
    }

    let query = baseUrl;
    if (filters.length > 0) {
      query = query + `&filters=[${filters.map((f) => `"${f}"`).join(',')}]`;
    }
    if (categories.length > 0) {
      query = query + `&categories=[${categories.map((f) => `"${f}"`).join(',')}]`;
    }
    if (fields.length > 0) {
      query = query + `&fields=[${fields.map((f) => `"${f}"`).join(',')}]`;
    }
    if (clickhouse_fields.length > 0) {
      query = query + `&clickhouse_fields=[${clickhouse_fields.map((f) => `"${f}"`).join(',')}]`;
    }
    allHeaders = {
      ...allHeaders,
      total_count: 'Total Events',
      CTU_Identified: 'CTU Identified',
      CTU_Transformed: 'CTU Transformed',
      CTU_Pending: 'Open CTU',
      SafaiMitra_Suraksha_Shivirs: 'SafaiMitra Suraksha Shivirs',
      Swachhata_Mein_Jan_Bhaagidari: 'Jan Bhagidari Activities',
    };
    try {
      // const url = `getCachedResultV1?url=${encodeURIComponent(query)}`;
      const response = await axios.get(query);
      // A newer fetch started while this was in flight. Returning here leaves
      // the table, headers and loading flag to that newer run.
      if (isStaleReport(reportTicket)) return;
      if (response.data) {
        // Process data to merge duplicate ministry codes
        const mergedData = mergeDuplicateMinistries(response.data);
        setTableData(mergedData);
        setHeaders(allHeaders);
      } else {
        setTableData([]);
        setHeaders({});
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching reports:', error);
      if (isStaleReport(reportTicket)) return;
      setIsLoading(false);
    }
  };

  const getReports2025 = async () => {
    const reportTicket = beginReportFetch();
    setIsLoading(true);
    setTableData([]);
    try {
      // Build filters object for API call
      const filters = {
        report_type: 'ministry_wise',
        // Without this the API falls back to its default year (2025), so a
        // 2026 request would silently return 2025 numbers.
        event_year: String(year),
      };
      if (selectedLocation) filters.location_type = selectedLocation;
      if (selectedStateCode) filters.state_code = selectedStateCode;
      if (selectedDistrictCode) filters.district_code = selectedDistrictCode;
      if (selectedULBCode) filters.ulb_code = selectedULBCode;
      if (selectedBlockCode) filters.block_code = selectedBlockCode;
      if (selectedGPCode) filters.gp_code = selectedGPCode;
      if (selectedVillageCode) filters.village_code = selectedVillageCode;
      if (ministryCode) filters.ministry_code = ministryCode;
      if (ministryDepartmentCode) filters.ministry_department_code = ministryDepartmentCode;
      if (ministrySubUnitCode) filters.ministry_unit_code = ministrySubUnitCode;
      if (selectedAreaType) filters.area_type = selectedAreaType;

      // Pass filters to the API function
      const response = await getCtuReport2025(filters);
      // A newer fetch started while this was in flight. Returning here leaves
      // the table, headers and loading flag to that newer run.
      if (isStaleReport(reportTicket)) return;
      if (response?.data) {
        let ministryData = response.data.data;

        // Filter out rows with empty ministry_name if the flag is true
        if (FILTER_EMPTY_MINISTRY_NAME) {
          ministryData = ministryData.filter(
            (row) => row.ministry_name && row.ministry_name.trim() !== ''
          );
        }

        // Only merge duplicate ministries when no ministry filters are applied (showing all ministries)
        if (!ministryCode && !ministryDepartmentCode && !ministrySubUnitCode) {
          ministryData = mergeDuplicateMinistries(ministryData);
        }

        console.log(ministryData, 'merged and filtered ministry data');

        // Set headers based on whether state is selected or not
        let headers = {
          // state_name: 'State Name',
          ministry_name: 'Ministry Name',
        };

        // Add ministry department name right after ministry name when ministry, department, or unit is selected
        if (ministryCode || ministryDepartmentCode || ministrySubUnitCode) {
          headers.ministry_department_name = 'Ministry Department Name';
        }

        // Add ministry unit name right after department name when ministry, department, or unit is selected
        if (ministryCode || ministryDepartmentCode || ministrySubUnitCode) {
          headers.ministry_unit_name = 'Ministry Unit Name';
        }

        // Add the rest of the columns
        headers = {
          ...headers,
          ctu_total_events: 'CTU Identified',
          ctu_completed_events: 'CTU Transformed',
          ctu_pending_events: 'Open CTU',
          low_intensity: 'Low Intensity CTU',
          medium_intensity: 'Medium Intensity CTU',
          high_intensity: 'High Intensity CTU',
          ctu_total_participants: 'CTU Participants',
          theme7_total_events: 'Clean Public Spaces Identified',
          theme7_completed_events: 'Public Spaces Cleaned',
          theme7_pending_events: 'Pending Public Spaces',
          theme7_total_participants: 'Clean Public Spaces Participants',
        };

        // Only show district_name and ulb_name when state is selected
        if (selectedStateCode && selectedLocation === 'URBAN') {
          headers = {
            // state_name: 'State Name',
            ministry_name: 'Ministry Name',
          };
          // Add ministry department name right after ministry name when ministry, department, or unit is selected
          if (ministryCode || ministryDepartmentCode || ministrySubUnitCode) {
            headers.ministry_department_name = 'Ministry Department Name';
          }
          // Add ministry unit name right after department name when ministry, department, or unit is selected
          if (ministryCode || ministryDepartmentCode || ministrySubUnitCode) {
            headers.ministry_unit_name = 'Ministry Unit Name';
          }
          // Add the rest of the columns
          headers = {
            ...headers,
            // district_name: 'District Name',
            // ulb_name: 'ULB Name',
            ctu_total_events: 'CTU Identified',
            ctu_completed_events: 'CTU Transformed',
            ctu_pending_events: 'Open CTU',
            low_intensity: 'Low Intensity CTU',
            medium_intensity: 'Medium Intensity CTU',
            high_intensity: 'High Intensity CTU',
            ctu_total_participants: 'CTU Participants',
            theme7_total_events: 'Clean Public Spaces Identified',
            theme7_completed_events: 'Public Spaces Cleaned',
            theme7_pending_events: 'Pending Public Spaces',
            theme7_total_participants: 'Clean Public Spaces Participants',
          };
        }

        if (selectedStateCode && selectedLocation === 'RURAL') {
          headers = {
            ministry_name: 'Ministry Name',
          };
          // Add ministry department name right after ministry name when ministry, department, or unit is selected
          if (ministryCode || ministryDepartmentCode || ministrySubUnitCode) {
            headers.ministry_department_name = 'Ministry Department Name';
          }
          // Add ministry unit name right after department name when ministry, department, or unit is selected
          if (ministryCode || ministryDepartmentCode || ministrySubUnitCode) {
            headers.ministry_unit_name = 'Ministry Unit Name';
          }
          // Add the rest of the columns
          headers = {
            ...headers,
            // state_name: 'State Name',
            // district_name: 'District Name',
            // block_name: 'Block Name',
            ctu_total_events: 'CTU Identified',
            ctu_completed_events: 'CTU Transformed',
            ctu_pending_events: 'Open CTU',
            low_intensity: 'Low Intensity CTU',
            medium_intensity: 'Medium Intensity CTU',
            high_intensity: 'High Intensity CTU',
            ctu_total_participants: 'CTU Participants',
            theme7_total_events: 'Clean Public Spaces Identified',
            theme7_completed_events: 'Public Spaces Cleaned',
            theme7_pending_events: 'Pending Public Spaces',
            theme7_total_participants: 'Clean Public Spaces Participants',
          };
        }
        if (selectedDistrictCode && selectedLocation === 'RURAL') {
          headers = {
            ministry_name: 'Ministry Name',
          };
          // Add ministry department name right after ministry name when ministry, department, or unit is selected
          if (ministryCode || ministryDepartmentCode || ministrySubUnitCode) {
            headers.ministry_department_name = 'Ministry Department Name';
          }
          // Add ministry unit name right after department name when ministry, department, or unit is selected
          if (ministryCode || ministryDepartmentCode || ministrySubUnitCode) {
            headers.ministry_unit_name = 'Ministry Unit Name';
          }
          // Add the rest of the columns
          headers = {
            ...headers,
            // state_name: 'State Name',
            // district_name: 'District Name',
            // block_name: 'Block Name',
            ctu_total_events: 'CTU Identified',
            ctu_completed_events: 'CTU Transformed',
            ctu_pending_events: 'Open CTU',
            low_intensity: 'Low Intensity CTU',
            medium_intensity: 'Medium Intensity CTU',
            high_intensity: 'High Intensity CTU',
            ctu_total_participants: 'CTU Participants',
            theme7_total_events: 'Clean Public Spaces Identified',
            theme7_completed_events: 'Public Spaces Cleaned',
            theme7_pending_events: 'Pending Public Spaces',
            theme7_total_participants: 'Clean Public Spaces Participants',
          };
        }

        if (selectedBlockCode && selectedLocation === 'RURAL') {
          headers = {
            ministry_name: 'Ministry Name',
          };
          // Add ministry department name right after ministry name when ministry, department, or unit is selected
          if (ministryCode || ministryDepartmentCode || ministrySubUnitCode) {
            headers.ministry_department_name = 'Ministry Department Name';
          }
          // Add ministry unit name right after department name when ministry, department, or unit is selected
          if (ministryCode || ministryDepartmentCode || ministrySubUnitCode) {
            headers.ministry_unit_name = 'Ministry Unit Name';
          }
          // Add the rest of the columns
          headers = {
            ...headers,
            // state_name: 'State Name',
            // district_name: 'District Name',
            // block_name: 'Block Name',
            // gp_name: 'GP Name',
            ctu_total_events: 'CTU Identified',
            ctu_completed_events: 'CTU Transformed',
            ctu_pending_events: 'Open CTU',
            low_intensity: 'Low Intensity CTU',
            medium_intensity: 'Medium Intensity CTU',
            high_intensity: 'High Intensity CTU',
            ctu_total_participants: 'CTU Participants',
            theme7_total_events: 'Clean Public Spaces Identified',
            theme7_completed_events: 'Public Spaces Cleaned',
            theme7_pending_events: 'Pending Public Spaces',
            theme7_total_participants: 'Clean Public Spaces Participants',
          };
        }

        if (selectedGPCode && selectedLocation === 'RURAL') {
          headers = {
            ministry_name: 'Ministry Name',
          };
          // Add ministry department name right after ministry name when ministry, department, or unit is selected
          if (ministryCode || ministryDepartmentCode || ministrySubUnitCode) {
            headers.ministry_department_name = 'Ministry Department Name';
          }
          // Add ministry unit name right after department name when ministry, department, or unit is selected
          if (ministryCode || ministryDepartmentCode || ministrySubUnitCode) {
            headers.ministry_unit_name = 'Ministry Unit Name';
          }
          // Add the rest of the columns
          headers = {
            ...headers,
            // state_name: 'State Name',
            // district_name: 'District Name',
            // block_name: 'Block Name',
            // gp_name: 'GP Name',
            // village_name: 'Village Name',
            ctu_total_events: 'CTU Identified',
            ctu_completed_events: 'CTU Transformed',
            ctu_pending_events: 'Open CTU',
            low_intensity: 'Low Intensity CTU',
            medium_intensity: 'Medium Intensity CTU',
            high_intensity: 'High Intensity CTU',
            ctu_total_participants: 'CTU Participants',
            theme7_total_events: 'Clean Public Spaces Identified',
            theme7_completed_events: 'Public Spaces Cleaned',
            theme7_pending_events: 'Pending Public Spaces',
            theme7_total_participants: 'Clean Public Spaces Participants',
          };
        }

        if (selectedVillageCode && selectedLocation === 'RURAL') {
          headers = {
            ministry_name: 'Ministry Name',
          };
          // Add ministry department name right after ministry name when ministry, department, or unit is selected
          if (ministryCode || ministryDepartmentCode || ministrySubUnitCode) {
            headers.ministry_department_name = 'Ministry Department Name';
          }
          // Add ministry unit name right after department name when ministry, department, or unit is selected
          if (ministryCode || ministryDepartmentCode || ministrySubUnitCode) {
            headers.ministry_unit_name = 'Ministry Unit Name';
          }
          // Add the rest of the columns
          headers = {
            ...headers,
            // state_name: 'State Name',
            // district_name: 'District Name',
            // block_name: 'Block Name',
            // gp_name: 'GP Name',
            // village_name: 'Village Name',
            ctu_total_events: 'CTU Identified',
            ctu_completed_events: 'CTU Transformed',
            ctu_pending_events: 'Open CTU',
            low_intensity: 'Low Intensity CTU',
            medium_intensity: 'Medium Intensity CTU',
            high_intensity: 'High Intensity CTU',
            ctu_total_participants: 'CTU Participants',
            theme7_total_events: 'Clean Public Spaces Identified',
            theme7_completed_events: 'Public Spaces Cleaned',
            theme7_pending_events: 'Pending Public Spaces',
            theme7_total_participants: 'Clean Public Spaces Participants',
          };
        }

        // 2026 drops the CTU intensity breakdown. The three columns are still
        // built in every header variant above and still returned by the API --
        // they are removed here, in one place, rather than edited out of each of
        // the seven variants, so 2025 and 2024 are untouched and putting them back
        // is a matter of deleting this block.
        //
        // Order matters: this runs after the variant selection and before
        // setHeaders, so it catches whichever variant was chosen. The totals row
        // below is keyed on `headers[column]`, so dropping the header also drops
        // the column from the Total, and TableComponent derives its export columns
        // from this same object.
        if (Number(year) === 2026) {
          delete headers.low_intensity;
          delete headers.medium_intensity;
          delete headers.high_intensity;
        }

        setHeaders(headers);

        // Add total row only on the API years
        if (isApiReportYear(year) && ministryData.length > 0) {
          const numericalColumns = [
            'ctu_total_events',
            'ctu_completed_events',
            'ctu_pending_events',
            'low_intensity',
            'medium_intensity',
            'high_intensity',
            'ctu_total_participants',
            'theme7_total_events',
            'theme7_completed_events',
            'theme7_pending_events',
            'theme7_total_participants',
          ];
          const totalRow = {};
          const firstColumn = Object.keys(headers)[0];
          totalRow[firstColumn] = 'Total';
          numericalColumns.forEach((column) => {
            if (headers[column]) {
              const total = ministryData.reduce(
                (sum, row) => sum + (parseInt(row[column]) || 0),
                0
              );
              totalRow[column] = total;
            }
          });
          setTableData([...ministryData, totalRow]);
        } else {
          setTableData(ministryData);
        }
      } else {
        setTableData([]);
        setHeaders({});
      }
    } catch (error) {
      console.error('Error fetching 2025 CTU report:', error);
      if (isStaleReport(reportTicket)) return;
      setTableData([]);
      setHeaders({});
    }
    setIsLoading(false);
  };

  const getEventTypeReports2025 = async () => {
    const reportTicket = beginReportFetch();
    setIsLoading(true);
    setTableData([]);
    try {
      // Build filters object for Event Type Report API call
      const filters = {
        report_type: 'ministry_wise',
        event_theme_id: selectedEventThemeCode,
        // See the note in getReports2025: the API defaults to 2025.
        event_year: String(year),
      };
      if (selectedLocation) filters.location_type = selectedLocation;
      if (selectedStateCode) filters.state_code = selectedStateCode;
      if (selectedDistrictCode) filters.district_code = selectedDistrictCode;
      if (selectedULBCode) filters.ulb_code = selectedULBCode;
      if (selectedBlockCode) filters.block_code = selectedBlockCode;
      if (selectedGPCode) filters.gp_code = selectedGPCode;
      if (selectedVillageCode) filters.village_code = selectedVillageCode;
      if (ministryCode) filters.ministry_code = ministryCode;
      if (ministryDepartmentCode) filters.ministry_department_code = ministryDepartmentCode;
      if (ministrySubUnitCode) filters.ministry_unit_code = ministrySubUnitCode;

      // Pass filters to the Event Type Report API function
      const response = await getEventTypeReport(filters);
      // A newer fetch started while this was in flight. Returning here leaves
      // the table, headers and loading flag to that newer run.
      if (isStaleReport(reportTicket)) return;
      if (response?.data?.data) {
        // Set dynamic headers based on the response data structure with proper column order
        let headers = {
          ministry_name: 'Ministry Name',
        };

        // Add ministry department and unit columns in proper hierarchy order before metrics
        if (ministryCode) {
          headers.ministry_department_name = 'Ministry Department Name';
        }
        if (ministryCode || ministryDepartmentCode) {
          headers.ministry_unit_name = 'Ministry Unit Name';
        }

        // Themes with a fixed column list for this campaign year (the SHS-2026
        // pillars) are laid out from that list rather than by the per-theme
        // blocks below, which cover the 2025 themes only. Without it a 2026
        // pillar would render a table of ministry names and no metrics.
        const pillarColumns = getEventTypeReportColumns(year, selectedEventThemeCode);
        if (pillarColumns) {
          headers = { ...headers, ...pillarColumns };
        }

        // Add special columns for Advocacy theme (event_theme_id = 6) after ministry columns
        if (!pillarColumns && (selectedEventThemeCode === 6 || selectedEventThemeCode === '6')) {
          // Calculated totals first
          headers.total_events_conducted = 'Total events conducted';
          headers.total_participants_sum = 'Total Participants';
          // Individual fields after
          headers.number_of_door_to_door_awareness_drive_conducted =
            'Number of Door to Door Awareness Drives Conducted';
          headers.door_to_door_total_participants = 'Door to Door Total Participants';
          headers.number_of_swachhata_rallies_conducted = 'Number of Swachhata Rallies Conducted';
          headers.swachhata_rallies_total_participants = 'Swachhata Rallies Total Participants';
          headers.number_of_competitions_for_swachhata_conducted =
            'Number of Competitions for Swachhata Conducted';
          headers.competitions_total_participants = 'Competitions Total Participants';
          headers.number_of_waste_to_art_installations_done = 'Waste to Art Installations Done';
          headers.quantity_of_waste_reused = 'Quantity of Waste Reused';
          headers.number_of_swachh_food_streets = 'Number of Swachh Food Streets';
          headers.swachh_food_streets_total_participants = 'Swachh Food Streets Total Participants';
          headers.number_of_sports_leagues_for_swachhata = 'Number of Sports Leagues for Swachhata';
          headers.sports_leagues_total_participants = 'Sports Leagues Total Participants';
          headers.number_of_temporary_rrr_centers_established =
            'Number of Temporary RRR Centers Established';
          headers.temporary_rrr_centers_total_participants =
            'Temporary RRR Centers Total Participants';
          headers.number_of_refurbishment_and_branding_of_sbm_assets =
            'Number of Refurbishment and Branding of SBM Assets';
          headers.refurbishment_sbm_assets_total_participants =
            'Refurbishment SBM Assets Total Participants';
          headers.number_of_say_no_to_single_use_plastic_drives =
            'Number of Say No to Single Use Plastic Drives';
          headers.plastic_drives_total_participants = 'Plastic Drives Total Participants';
        }

        // Add special columns for Clean Green Utsav theme (event_theme_id = 5) after ministry columns
        if (selectedEventThemeCode === 5 || selectedEventThemeCode === '5') {
          headers.total_events_conducted = 'Total Events Conducted';
          headers.total_number_of_eco_pandal = 'Total Number of Eco Pandal';
          headers.total_number_of_swachhata_rangolis = 'Total Number of Swachhata Rangolis';
          headers.total_number_of_post_festive_cleanliness_drives =
            'Total Number of Post Festive Cleanliness Drives';
        }

        // Add special columns for SafaiMitra theme (event_theme_id = 3) after ministry columns - same as location wise
        if (selectedEventThemeCode === 3 || selectedEventThemeCode === '3') {
          headers.total_number_of_shivirs_conducted = 'Shivirs Conducted';
          headers.total_sanitation_workers_participated = 'Sanitation workers participated';
          headers.no_of_sanitation_workers_recognition = 'Sanitation workers recognized';
          headers.no_of_preventive_health_checkups_organized =
            'Preventive health checkups organized';
          headers.health_participants = 'Health checkup participants';
          headers.no_ppe_kits_safety_gear_distributed = 'PPE kits distributed';
          headers.total_beneficiaries = 'Scheme Beneficiaries';
          headers.total_health_ppe_beneficiaries = 'Total Beneficiaries';
        }

        // Process and set table data
        let processedData = response.data.data;

        // Filter out rows with empty ministry_name if the flag is true
        if (FILTER_EMPTY_MINISTRY_NAME) {
          processedData = processedData.filter(
            (row) => row.ministry_name && row.ministry_name.trim() !== ''
          );
        }

        // Only merge duplicate ministries when no ministry filters are applied (showing all ministries)
        if (!ministryCode && !ministryDepartmentCode && !ministrySubUnitCode) {
          processedData = mergeDuplicateMinistries(processedData);
        }

        // Process data to add calculated columns for specific themes. A theme
        // with a fixed column list derives nothing: every pillar column is
        // reported as-is.
        if (!pillarColumns && (selectedEventThemeCode === 6 || selectedEventThemeCode === '6')) {
          // Advocacy theme processing - same logic as location wise
          processedData = processedData.map((row) => {
            // Calculate total events conducted by summing all event-related numeric fields
            // Exclude 'number_of_waste_to_art_installations_done' from total events
            let totalEventsKeys = Object.keys(row).filter(
              (key) =>
                key.includes('number_of_') &&
                !key.includes('participants') &&
                !key.includes('males') &&
                !key.includes('females') &&
                key !== 'number_of_waste_to_art_installations_done' &&
                typeof row[key] === 'number'
            );

            const totalEvents = totalEventsKeys.reduce(
              (sum, key) => sum + (parseInt(row[key]) || 0),
              0
            );

            // Calculate total participants by summing participant-related fields
            // Exclude 'quantity_of_waste_reused' from total participants
            let participantKeys = Object.keys(row).filter(
              (key) =>
                (key.includes('participants') || key.includes('total_participants')) &&
                key !== 'quantity_of_waste_reused' &&
                typeof row[key] === 'number'
            );

            const totalParticipants = participantKeys.reduce(
              (sum, key) => sum + (parseInt(row[key]) || 0),
              0
            );

            return {
              ...row,
              total_events_conducted: totalEvents,
              total_participants_sum: totalParticipants,
            };
          });
        }

        // Clean Green Utsav theme processing
        if (selectedEventThemeCode === 5 || selectedEventThemeCode === '5') {
          processedData = processedData.map((row) => {
            // Calculate total events conducted by summing Clean Green Utsav specific fields
            const totalEvents =
              (parseInt(row.total_number_of_eco_pandal) || 0) +
              (parseInt(row.total_number_of_swachhata_rangolis) || 0) +
              (parseInt(row.total_number_of_post_festive_cleanliness_drives) || 0);

            return {
              ...row,
              total_events_conducted: totalEvents,
            };
          });
        }

        // SafaiMitra theme processing - same as location wise
        if (selectedEventThemeCode === 3 || selectedEventThemeCode === '3') {
          processedData = processedData.map((row) => {
            // Calculate sum of no_of_sanitation_workers_recognition + health_participants + no_ppe_kits_safety_gear_distributed + total_beneficiaries
            const sanitationWorkersRecognition =
              parseInt(row.no_of_sanitation_workers_recognition) || 0;
            const healthParticipants = parseInt(row.health_participants) || 0;
            const ppeKitsDistributed = parseInt(row.no_ppe_kits_safety_gear_distributed) || 0;
            const totalBeneficiaries = parseInt(row.total_beneficiaries) || 0;

            const totalHealthPpeBeneficiaries =
              sanitationWorkersRecognition +
              healthParticipants +
              ppeKitsDistributed +
              totalBeneficiaries;

            return {
              ...row,
              total_health_ppe_beneficiaries: totalHealthPpeBeneficiaries,
            };
          });
        }

        // Calculate totals for numeric columns, display '-' for string columns
        if (processedData.length > 0) {
          const firstColumn = Object.keys(headers)[0];
          const totalRow = { [firstColumn]: 'Total' };

          Object.keys(headers).forEach((column) => {
            if (column !== firstColumn) {
              // Check if this is a ministry column (string values)
              const ministryColumns = ['ministry_department_name', 'ministry_unit_name'];

              if (ministryColumns.includes(column)) {
                // Display '-' for ministry/string columns
                totalRow[column] = '-';
              } else {
                // Calculate totals for numeric columns
                const total = processedData.reduce((sum, row) => {
                  const value = row[column];
                  return sum + (typeof value === 'number' ? value : parseInt(value) || 0);
                }, 0);
                totalRow[column] = total;
              }
            }
          });

          // Add total row at the end
          processedData.push(totalRow);
        }

        setHeaders(headers);
        setTableData(processedData);
      } else {
        setTableData([]);
        setHeaders({});
      }
    } catch (error) {
      console.error('Error fetching Event Type reports:', error);
      if (isStaleReport(reportTicket)) return;
      setTableData([]);
      setHeaders({});
    }
    setIsLoading(false);
  };

  // Helper function to decide which API to call. Both 2025-named helpers take
  // the campaign year from the `year` prop and serve every API year.
  const getReports2025Handler = () => {
    if (!isApiReportYear(year)) {
      // Call 2024 reports
      getReports();
    } else if (selectedEventThemeCode && selectedEventThemeCode !== 'all') {
      // Call Event Type Report API for specific event themes
      getEventTypeReports2025();
    } else {
      // Call original CTU Report API for 'all' or default
      getReports2025();
    }
  };

  // Handle View Report button click
  const handleViewReport = async () => {
    setReportLoading(true);
    try {
      // Call the appropriate report function based on role and selected filters
      if (!role?.info?.role_id || isNationalScopeRole(role?.info?.role_id)) {
        await getReports2025Handler();
      } else if (role?.info?.role_id === 2 && ministryCode) {
        await getReports2025Handler();
      } else if (role?.info?.role_id === 3 && ministryDepartmentCode) {
        await getReports2025Handler();
      } else if (role?.info?.role_id === 4 && ministrySubUnitCode) {
        await getReports2025Handler();
      } else if (role?.info?.role_id === 5 && selectedStateCode) {
        await getReports2025Handler();
      } else if (role?.info?.role_id === 6 && selectedDistrictCode) {
        await getReports2025Handler();
      } else if (role?.info?.role_id === 7 && selectedULBCode) {
        await getReports2025Handler();
      } else if (role?.info?.role_id === 8 && selectedBlockCode) {
        await getReports2025Handler();
      } else if (role?.info?.role_id === 13 && ministryCode) {
        await getReports2025Handler();
      } else {
        // If conditions not met, still allow report generation
        await getReports2025Handler();
      }
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setReportLoading(false);
    }
  };

  // Load initial default data only once when component mounts
  useEffect(() => {
    if (!role?.info?.role_id || isNationalScopeRole(role?.info?.role_id)) {
      getReports2025Handler();
    }
  }, [role?.info?.role_id, year]);

  // Load event themes on component mount
  useEffect(() => {
    getEventTheme();
  }, [year]);

  // Reset selectedEventThemeCode when year changes. The years do not offer the
  // same themes -- Clean Green Utsav is 2025-only, the pillars are 2026-only --
  // so a carried-over selection could leave the dropdown on a theme its own
  // option list no longer contains.
  useEffect(() => {
    setSelectedEventThemeCode(isApiReportYear(year) ? 'all' : undefined);
  }, [year]);

  return (
    <Grid container spacing={2} mb={2} mt={2}>
      {/* Event Theme Name Dropdown - only on the API years */}
      {isApiReportYear(year) && eventTypes?.length > 0 && (
        <Grid item xs={12} md={3}>
          <CustomFormLabel>Event Theme Name</CustomFormLabel>
          <CustomSelect
            value={selectedEventThemeCode || ''}
            onChange={handleEventThemeChange}
            id="event_theme_id"
            fullWidth
            name="event_theme_id"
            size="small"
          >
            {eventTypes.map((data) => (
              <MenuItem value={data.value} key={data.value}>
                {data.name}
              </MenuItem>
            ))}
          </CustomSelect>
        </Grid>
      )}
      <Ministry
        ministryCode={ministryCode}
        setMinistryCode={setMinistryCode}
        ministryDepartmentCode={ministryDepartmentCode}
        setMinistryDepartmentCode={setMinistryDepartmentCode}
        ministrySubUnitCode={ministrySubUnitCode}
        setMinistrySubUnitCode={setMinistrySubUnitCode}
      />
      {role?.info?.role_id !== 13 && (
        <LGD
          eventYear={year}
          selectedLocation={selectedLocation}
          setSelectedLocation={setSelectedLocation}
          selectedStateCode={selectedStateCode}
          setSelectedStateCode={setSelectedStateCode}
          selectedDistrictCode={selectedDistrictCode}
          setSelectedDistrictCode={setSelectedDistrictCode}
          selectedBlockCode={selectedBlockCode}
          setSelectedBlockCode={setSelectedBlockCode}
          selectedULBCode={selectedULBCode}
          setSelectedULBCode={setSelectedULBCode}
          selectedGPCode={selectedGPCode}
          setSelectedGPCode={setSelectedGPCode}
          selectedVillageCode={selectedVillageCode}
          setSelectedVillageCode={setSelectedVillageCode}
        />
      )}
      {/* Hide Event component on the API years -- their theme dropdown above
          replaces it. */}
      {!isApiReportYear(year) && (
        <Event
          selectedEventThemeCode={selectedEventThemeCode}
          setSelectedEventThemeCode={setSelectedEventThemeCode}
          selectedEventNameCode={selectedEventNameCode}
          setSelectedEventNameCode={setSelectedEventNameCode}
          selectedEventHeadingCode={selectedEventHeadingCode}
          setSelectedEventHeadingCode={setSelectedEventHeadingCode}
        />
      )}

      {selectedEventNameCode === 34 && (
        <Grid item xs={12} md={3}>
          <CustomFormLabel>CTU Status</CustomFormLabel>
          <CustomSelect
            value={selectedCtuStatus !== undefined ? selectedCtuStatus : ''}
            onChange={(e) =>
              setSelectedCtuStatus(e.target.value === '' ? undefined : e.target.value === 'true')
            }
            id="ctu_status"
            fullWidth
            name="ctu_status"
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">CTU Transformed</MenuItem>
            <MenuItem value="false">Open CTU</MenuItem>
          </CustomSelect>
        </Grid>
      )}

      {/* Area Type dropdown - only visible for admin users (role_id: 1) and when CTU & CPS is selected */}
      {isNationalScopeRole(role?.info?.role_id) &&
        selectedEventThemeCode === 'all' &&
        isApiReportYear(year) && (
          <Grid item xs={12} md={3}>
            <CustomFormLabel>Area Type</CustomFormLabel>
            <CustomSelect
              value={selectedAreaType || ''}
              onChange={(e) =>
                setSelectedAreaType(e.target.value === '' ? undefined : e.target.value)
              }
              id="area_type"
              fullWidth
              name="area_type"
              size="small"
            >
              <MenuItem value="">All</MenuItem>
              {areaTypes.map((areaType, index) => (
                <MenuItem key={index} value={areaType}>
                  {areaType}
                </MenuItem>
              ))}
            </CustomSelect>
          </Grid>
        )}

      {/* View Report Button */}
      {isApiReportYear(year) && (
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={handleViewReport}
            loading={reportLoading}
            size="large"
            sx={{ minWidth: 200 }}
          >
            View Report
          </LoadingButton>
        </Grid>
      )}

      <Card sx={{ width: '100%' }}>
        <CardContent>
          <Box
            sx={{
              width: '100%',
              overflow: 'auto',
            }}
          >
            {tableData?.length > 0 ? (
              <TableComponent
                data={tableData}
                mainHeaders={headers}
                fileName={'reports'}
                year={year}
                showPagination={false}
              />
            ) : (
              <Typography variant="h3">No data yet</Typography>
            )}
          </Box>
        </CardContent>
      </Card>
      {selectedEventNameCode === 34 && ministrySubUnitCode && (
        <Card sx={{ width: '100%' }}>
          <CardContent>
            <Box
              sx={{
                width: '100%',
                overflow: 'auto',
              }}
            >
              {loading ? (
                <Spinner />
              ) : (
                <>
                  {events.length > 0 ? (
                    <TableComponent
                      mainHeaders={role?.info?.role_id === 10 ? roleRow : allRow}
                      data={events}
                      fileName={'CTU Details'}
                      downloadRow={downloadRow}
                      getData={() => {}}
                      isPublic
                      handleNext={() => {
                        if (Object.keys(filters).length > 0) {
                          getFilteredData(false);
                        } else {
                        }
                      }}
                    />
                  ) : (
                    <Typography variant="h3">No data yet</Typography>
                  )}
                </>
              )}
            </Box>
          </CardContent>
        </Card>
      )}
    </Grid>
  );
};

export default MinistryWiseReport;
